import type { Theme } from "../types";
import { getContrastColor } from "./detectColor";
import { getProgramConstants } from "../programConstants";
/**
 * THEME COLOR MANAGEMENT PROCEDURE
 *
 * 1. PRIMARY COLOR USAGE:
 *    - Most UI elements should use `--color-brand-primary` as background color or text color
 *    - This includes buttons, cards, headers, active states, highlights, etc.
 *    - Use `--color-inverse-foreground` for text on primary backgrounds
 *
 * 2. SECONDARY COLOR USAGE:
 *    - `--color-brand-secondary` is a fallback option for places where primary doesn't work
 *    - Use when primary color creates poor contrast or visual conflicts
 *    - Typically for subtle backgrounds, secondary buttons, or complementary elements
 *
 * 3. COLOR REPLACEMENT STRATEGY:
 *    - Replace colors close to gray with `--color-secondary-foreground` or `--color-disabled`
 *    - Replace colors close to white with `--color-inverse-foreground`
 *    - Replace colors close to black with `--color-primary-foreground`
 *    - Use `--color-surface` for neutral background surfaces
 *    - Use `--color-disabled` for disabled states and subtle elements
 *
 * 4. OPACITY ADJUSTMENTS:
 *    - If colors don't look good in UI, use opacity variations:
 *      - CSS: `color-mix(in srgb, var(--color-brand-primary) 80%, transparent 20%)`
 *      - CSS: `color-mix(in srgb, var(--color-primary-foreground) 60%, transparent 40%)`
 *      - Tailwind: `bg-brand-primary/20`, `text-primary-foreground/60`
 *    - Adjust opacity percentages as needed for visual hierarchy
 *
 * 5. TAILWIND 4 CONFIGURATION:
 *    To add any custom variable add these to your global.css file under the @theme directive:
 *    ```css
 *    @theme {
 *      --color-brand-primary: var(--color-brand-primary);
 *      --color-brand-secondary: var(--color-brand-secondary);
 *      --color-primary-foreground: var(--color-primary-foreground);
 *      --color-secondary-foreground: var(--color-secondary-foreground);
 *      --color-inverse-foreground: var(--color-inverse-foreground);
 *      --color-surface: var(--color-surface);
 *      --color-disabled: var(--color-disabled);
 *    }
 *    ```
 *
 * 6. TAILWIND USAGE EXAMPLES:
 *    - Backgrounds: `bg-brand-primary`, `bg-brand-secondary`, `bg-surface`
 *    - Text: `text-primary-foreground`, `text-inverse-foreground`, `text-secondary-foreground`
 *    - Borders: `border-brand-primary`, `border-disabled`
 *    - With opacity: `bg-brand-primary/80`, `text-primary-foreground/60`
 *    - Hover states: `hover:bg-brand-primary/90`, `hover:text-inverse-foreground`
 *
 * 7. IMPLEMENTATION REQUIREMENTS:
 *    - All CSS variables must be declared under the `:root` theme decorator
 *    - Ensure fallback values are provided for all custom properties
 *    - Configure Tailwind 4 colors using @theme directive (see section 5)
 *
 * 8. COLOR MAPPING REFERENCE:
 *    - Black (#000000) → `--color-primary-foreground` / `text-primary-foreground`
 *    - White (#FFFFFF) → `--color-inverse-foreground` / `text-inverse-foreground`
 *    - Gray tones → `--color-secondary-foreground` / `text-secondary-foreground`
 *    - Primary brand elements → `--color-brand-primary` / `bg-brand-primary`
 *    - Secondary/fallback elements → `--color-brand-secondary` / `bg-brand-secondary`
 *
 * TODO: Implement dynamic color brightness detection
 * - Create utility function to determine if `--color-brand-primary` is dark/light
 * - Automatically switch `--color-primary-foreground` and `--color-inverse-foreground`
 *   based on primary color luminance for optimal contrast
 * - This will ensure proper text readability across all theme variations
 *
 * TODO: Global color replacement audit
 * - Search codebase for hardcoded black (#000, #000000) → replace with `text-primary-foreground`
 * - Search codebase for hardcoded white (#fff, #ffffff) → replace with `text-inverse-foreground`
 * - Update all gray variations to use appropriate CSS variables
 */

const PROGRAM_CONSTANTS = getProgramConstants(
  process.env.NEXT_PROGRAM_NAME || "",
);

export const DEFAULT_THEME_COLORS = {
  brandPrimary: PROGRAM_CONSTANTS.primaryColor,
  brandSecondary: PROGRAM_CONSTANTS.secondaryColor,
};

// TODO: Refactor this after integrating api call
const NEUTRAL_COLORS = {
  gray: "#3C3C3C",
  disabled: "#B0BABD",
  white: "#FFFFFF",
  surface: "#F3F7FF",
};
const TEXT_COLORS = {
  primary: "#000000",
  secondary: NEUTRAL_COLORS.gray,
  inverse: NEUTRAL_COLORS.white,
};

// Generate CSS from theme
export function generateThemeCSS(theme: Theme): string {
  const textOnPrimary = getContrastColor(theme.brandPrimary);
  const textOnSecondary = getContrastColor(theme.brandSecondary);

  return `:root {
    --color-brand-primary: ${theme.brandPrimary};
    --color-brand-secondary: ${theme.brandSecondary};
    --color-primary-foreground: ${TEXT_COLORS.primary};
    --color-secondary-foreground: ${TEXT_COLORS.secondary};
    --color-inverse-foreground: ${TEXT_COLORS.inverse};
    --color-surface: ${NEUTRAL_COLORS.surface};
    --color-disabled: ${NEUTRAL_COLORS.disabled};
    --color-primary-text: ${textOnPrimary};
    --color-secondary-text: ${textOnSecondary};
  }`;
}
